type State =
  | 'code'
  | 'single-line-comment'
  | 'multi-line-comment'
  | 'single-string'
  | 'double-string'
  | 'template-literal';

/**
 * Character-by-character state machine that strips comments and/or strings
 * from JavaScript code while respecting context boundaries.
 *
 * Unlike regex-based approaches, this correctly handles:
 * - URLs inside strings (https://)
 * - Comment-like patterns inside strings ("// not a comment")
 * - String delimiters inside comments
 * - Escaped characters (\" inside strings)
 */
const strip = (code: string, removeStrings: boolean): string => {
  let state: State = 'code';
  let result = '';
  let i = 0;

  while (i < code.length) {
    const ch = code[i];
    const next = code[i + 1];

    switch (state) {
      case 'code':
        if (ch === '/' && next === '/') {
          state = 'single-line-comment';
          i += 2;
        } else if (ch === '/' && next === '*') {
          state = 'multi-line-comment';
          i += 2;
        } else if (ch === "'") {
          state = 'single-string';
          result += removeStrings ? ' ' : ch;
          i++;
        } else if (ch === '"') {
          state = 'double-string';
          result += removeStrings ? ' ' : ch;
          i++;
        } else if (ch === '`') {
          state = 'template-literal';
          result += removeStrings ? ' ' : ch;
          i++;
        } else {
          result += ch;
          i++;
        }
        break;

      case 'single-line-comment':
        if (ch === '\n') {
          state = 'code';
          result += '\n';
          i++;
        } else {
          i++;
        }
        break;

      case 'multi-line-comment':
        if (ch === '*' && next === '/') {
          state = 'code';
          i += 2;
        } else {
          // Preserve newlines for line-number accuracy
          if (ch === '\n') {
            result += '\n';
          }
          i++;
        }
        break;

      case 'single-string':
        if (ch === '\\') {
          result += removeStrings ? '  ' : ch + (next || '');
          i += 2;
        } else if (ch === "'") {
          state = 'code';
          result += removeStrings ? ' ' : ch;
          i++;
        } else {
          result += removeStrings ? ' ' : ch;
          i++;
        }
        break;

      case 'double-string':
        if (ch === '\\') {
          result += removeStrings ? '  ' : ch + (next || '');
          i += 2;
        } else if (ch === '"') {
          state = 'code';
          result += removeStrings ? ' ' : ch;
          i++;
        } else {
          result += removeStrings ? ' ' : ch;
          i++;
        }
        break;

      case 'template-literal':
        if (ch === '\\') {
          result += removeStrings ? '  ' : ch + (next || '');
          i += 2;
        } else if (ch === '`') {
          state = 'code';
          result += removeStrings ? ' ' : ch;
          i++;
        } else {
          result += removeStrings ? ' ' : ch;
          i++;
        }
        break;
    }
  }

  return result;
};

/**
 * Strips only comments from code. Preserves strings, URLs, and regex patterns.
 * Used by the test runner to sanitize user code before execution.
 */
export const stripComments = (code: string): string => strip(code, false);

/**
 * Strips both comments AND string contents from code.
 * String delimiters are preserved but contents replaced with spaces.
 * Used for framework keyword detection to avoid false positives.
 */
export const stripCommentsAndStrings = (code: string): string => strip(code, true);
